import { FocusMonitor } from '@angular/cdk/a11y';
import { Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, Optional, Self, ViewChild, computed, effect, signal } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTable, MatTableModule } from '@angular/material/table';
import { Subject } from 'rxjs';
import { VariableType } from './Models/Enums/variable-type';
import { ContextualMenuActions } from './Models/contextual-menu-actions';
import { VariableElement } from './Models/variable-element';
import { VariableFormDialogResult } from './Models/variable-form-dialog-result';
import { VariableFormDialogComponent } from './variable-form-dialog/variable-form-dialog.component';

/**
 * Composant de saisie des en-têtes d'une requête HTTP.
 * (Utilisable dans un formulaire et dans un MatFormField)
 */
@Component({
    selector: 'app-variables-form',
    imports: [MatTableModule, MatIconModule, MatMenuModule, MatButtonModule],
    templateUrl: './variables-form.component.html',
    styleUrl: './variables-form.component.scss',
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: VariablesFormComponent,
        },
    ],
})
export class VariablesFormComponent implements OnInit, OnDestroy, MatFormFieldControl<VariableElement[]> {
    /**
     * Liste des actions du menu contextuel du tableau des variables.
     */
    public ContextualMenuActions = ContextualMenuActions;

    /**
     * Liste des types de variable et leur libellé.
     */
    public variableTypes: Map<VariableType, string> = new Map<VariableType, string>();

    /**
     * Liste des colonnes affichées dans le tableau des variables.
     */
    displayedColumns: string[] = ['name', 'type', 'value'];

    /**
     * Liste des variables affichées dans le tableau.
     */
    variables = signal<VariableElement[]>([]);

    /**
     * Emet lorsque des changements surviennent dans le composant pour prevenir le MatFormField parent de déclencher le change detection.
     */
    stateChanges = new Subject<void>();

    /**
     * Associe les id des elements dont le contenu sera utilisé pour décrire ce composant à l'attribut aria.
     */
    @HostBinding('attr.aria-describedby') describedBy = '';

    /**
     * Définit les id des elements dont le contenu sera utilisé pour décrire ce composant.
     * @param ids Id des elements dont le contenu sera utilisé pour décrire ce composant.
     */
    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join('');
    }

    /**
     * Définit la valeur du contrôle.
     */
    @Input()
    set value(value: VariableElement[]) {
        this.variables.set(value);
        this.stateChanges.next();
    }

    /**
     * Obtient la valeur du contrôle.
     */
    get value(): VariableElement[] {
        return this.variables();
    }

    /**
     * Valeur de l'identifiant qui sera attribuée à la prochaine instance du contrôle.
     */
    static nextId = 0;

    /**
     * Définit la valeur du placeholder.
     */
    set placeholder(placeholder: string) {
        // Le placeholder n'est pas utilisé dans ce composant
    }

    /**
     * Obtient la valeur du placeholder.
     */
    get placeholder() {
        return null;
    }

    /**
     * Retourne une valeur indiquant si le contrôle est vide (absence de variables)
     */
    get empty(): boolean {
        return this.variables().length !== 0;
    }

    /**
     * Retourne une valeur indiquant si le libellé doit être flottant.
     * (non géré)
     */
    get shouldLabelFloat(): boolean {
        return false;
    }

    /**
     * Obtient une valeur indiquant si ce contrôle est obligatoire.
     */
    @Input()
    get required() {
        return this._required;
    }

    /**
     * Définit une valeur indiquant si ce contrôle est obligatoire.
     */
    set required(value: boolean) {
        this._required = value;
        this.stateChanges.next();
    }

    /**
     * Valeur indiquant si le contrôle est obligatoire.
     */
    private _required = false;

    /**
     * Obtient une valeur indiquant si ce contrôle est désactivé.
     */
    @Input()
    get disabled() {
        return this._disabled;
    }

    /**
     * Définit une valeur indiquant si ce contrôle est désactivé.
     */
    set disabled(value: boolean) {
        this._disabled = value;

        let actionColumnIndex = this.displayedColumns.findIndex((c) => c === 'actions');

        if (value === false) {
            if (actionColumnIndex === -1) {
                this.displayedColumns.push('actions');
            }
        } else {
            if (actionColumnIndex !== -1) {
                this.displayedColumns.splice(actionColumnIndex, 1);
            }
        }
    }

    /**
     * Valeur indiquant si ce contrôle est désactivé.
     */
    private _disabled = false;

    /**
     * Valeur indiquant si le contrôle possède le focus.
     */
    _focused: boolean = false;

    /**
     * Obtient une valeur indiquant si le contrôle possède le focus.
     */
    get focused() {
        return this._focused;
    }

    /**
     * Définit une valeur indiquant si le contrôle possède le focus.
     */
    set focused(value: boolean) {
        this._focused = value;
        this.onTouched(value);
    }

    /**
     * Id du contrôle.
     */
    @HostBinding()
    id = `VariablesFormComponent-${VariablesFormComponent.nextId++}`;

    /**
     * Réference à l'élément MatTable du template.
     */
    @ViewChild(MatTable, { read: ElementRef, static: true })
    table: ElementRef;

    /**
     * Constructeur.
     * @param focusMonitor : Service utilisé pour écouter les changements de focus.
     * @param dialog : Service utilisé pour ouvrir des boites de dialogue.
     * @param ngControl : Reference au contrôle dans le formulaire.
     */
    constructor(
        private focusMonitor: FocusMonitor,
        public dialog: MatDialog,
        @Optional() @Self() public ngControl: NgControl
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }

        this.variableTypes.set(VariableType.Regex, 'Expression Régulière');
        this.variableTypes.set(VariableType.JsonPath, 'Chemin JSON');
        this.variableTypes.set(VariableType.FixedValue, 'Valeur fixe');

        computed(() => console.log('variables', this.variables));
        effect(() => {
            if (this.onChange) {
                this.onChange(this.variables());
            }
        });
    }

    /**
     * Renseigne la valeur du contrôle.
     * (Appellé par le formulaire)
     * @param value : valeur du contrôle.
     */
    writeValue(value: VariableElement[]): void {
        this.value = value;
    }

    /**
     * Fonction à appeler lorsque la valeur du contrôle change.
     */
    private onChange;

    /**
     * Inscrit une fonction à appeler lorsque la valeur du contrôle change.
     * (Appellé par le formulaire)
     * @param fn fonction à appeler lorsque la valeur du contrôle change.
     */
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    /**
     * Fonction à appeler lorsque le contrôle gagne le focus.
     */
    private onTouched;

    /**
     * Inscrit une fonction à appeler lorsque le contrôle gagne le focus.
     * (Appellé par le formulaire)
     * @param fn fonction à appeler lorsque le contrôle gagne le focus.
     */
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /**
     * Specifie l'état 'désactivé' du contrôle.
     * (Appellé par le formulaire)
     * @param value : Valeur de l'état 'désactivé'.
     */
    setDisabledState(value: boolean): void {
        this.disabled = value;
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le contrôle.
     * (Appellé par le formulaire)
     * @param event evnement clic de la souris.
     */
    onContainerClick(event: MouseEvent): void {
        // On ne fait rien de particulier lorsque l'on clique sur le tableau.
    }

    /**
     * Retourne une valeur indiquant si le contrôle est en erreur.
     */
    get errorState(): boolean {
        // On considère que ce control n'est jamais en erreur.
        return false;
    }

    /**
     * Appellé lors de l'initialisation du contrôle.
     */
    ngOnInit(): void {
        this.focusMonitor.monitor(this.table).subscribe((focused) => {
            this.focused = !!focused;
            this.stateChanges.next();
        });
    }

    /**
     * Appellé lors de la destruction du contrôle.
     */
    ngOnDestroy(): void {
        this.focusMonitor.stopMonitoring(this.table);
        this.stateChanges.complete();
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Editer' du menu contextuel.
     * @param editedVariable : Instance de la variable a éditer.
     */
    public onEditVariable(editedVariable: VariableElement): void {
        console.log('edit', editedVariable);

        const dialogRef = this.dialog.open(VariableFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: 'Édition de la variable',
                item: editedVariable,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: VariableFormDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                // Mise à jour de la variable.
                console.log('update', dialogResult.item);
                this.variables.update((variable) => {
                    editedVariable.name = dialogResult.item.name;
                    editedVariable.type = dialogResult.item.type;
                    editedVariable.value = dialogResult.item.value;

                    return variable;
                });
            }
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton de création d'une variable.
     */
    public onCreateVariable(): void {
        console.log('create');

        const dialogRef = this.dialog.open(VariableFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Ajout d'une variable",
                item: null,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: VariableFormDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                // Ajout de la variable.
                console.log('created', dialogResult.item);
                this.variables.update((variables) => {
                    return [...variables, dialogResult.item];
                });
            }
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Supprimer' du menu contextuel.
     * @param event : Instance de la variable a supprimer.
     */
    public onDeleteVariable(event: VariableElement): void {
        console.log('delete', event);
        this.variables.update((variables) => variables.filter((variable) => variable.name !== event.name));
    }
}
